import React, { useState, useEffect, useContext, useMemo } from 'react';
import '../page-list/PageList.css';
import styles from '../user-account/UserAccount.module.scss';
import styles2 from '../individual/IndividualPage.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { getDateFromZulu } from '../helpers/getDateFromZulu';
import { FilterModal, useFilter } from '../helpers/FilterComponent';
import { getClientUsers, assignUsers } from '../../services/clientService';
import { fetchAdminUsers } from '../../services/systemService';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const AssignUser = () => {
    const navigate = useNavigate();

    const { clientId } = useParams();

    const [userAccounts, setUserAccounts] = useState([]);
    const [rowsClicked, setRowsClicked] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'lastName',
        direction: SortDirections.ASCENDING,
    });
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(userAccounts);
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});

    const openModal = column => {
        setActiveModal(column);
    };
    const closeModal = () => {
        setActiveModal(null);
    };
    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };
    const clearFilters = () => {
        setSelections({});
        clearAllFilters();
        //Object.keys(selections).forEach(column => applyFilter(column, []));
    };
    // Compute available options considering active filters for current modal
    const getOptions = column => {
        // Remove the filter effect of the current column from the selections state
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column]; // Remove the current column's filter

        // Use these modified filters to filter data
        const filteredData = userAccounts.filter(userAccount =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(userAccount[key]),
            ),
        );

        // Now get all unique values for the current column from this filtered dataset
        return Array.from(new Set(filteredData.map(item => item[column])));
    };

    const [searchParams, setSearchParams] = useState({
        lastName: '',
        firstName: '',
        //agency: "",
    });

    const [searchInitialized, setSearchInitialized] = useState(false);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    function sanitizeSearchParams(searchParams) {
        const sanitizedParams = {};

        for (let key in searchParams) {
            const value = searchParams[key];

            if (value !== '' && value.length !== 0) {
                sanitizedParams[key] = value;
            }
        }

        return sanitizedParams;
    }

    const performSearch = async () => {
        try {
            const results = await fetchAdminUsers(
                sanitizeSearchParams(searchParams),
            );
            const assignedUsers = await getClientUsers(clientId);
            //Getting only users not assigned to this client yet
            const filteredUnassignedUsers = results.filter(user => {
                return !assignedUsers.some(
                    assignedUser => assignedUser.userId === user.id,
                );
            });
            const processedResponse = filteredUnassignedUsers.map(item => {
                return {
                    ...item,
                    status: getStatusString(item.tenantUserStatusId),
                };
            });
            setSearchInitialized(true);
            setUserAccounts(processedResponse);
        } catch (error) {
            console.error('Error performing user search:', error);
        }
    };

    const handleSubmit = e => {
        const isSearchParamsEmpty = Object.values(searchParams).every(
            value => value.length === 0,
        );
        if (isSearchParamsEmpty) {
            Toast.error(getText('msg_7570a3163e95'));
        } else {
            performSearch();
        }
    };

    const handleFormSubmit = e => {
        e.preventDefault();
        handleSubmit();
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    /*useEffect(() => {
        loadUserAccounts();
    }, []);*/

    const loadUserAccounts = async () => {
        try {
            const assignedUsersPromise = getClientUsers(clientId);
            const allUsersPromise = fetchAdminUsers({});

            const [assignedUsers, allUsers] = await Promise.all([
                assignedUsersPromise,
                allUsersPromise,
            ]);

            //Getting only users not assigned to this client yet
            const filteredUnassignedUsers = allUsers.filter(user => {
                return !assignedUsers.some(
                    assignedUser => assignedUser.userId === user.id,
                );
            });

            const processedResponse = filteredUnassignedUsers.map(item => {
                return {
                    ...item,
                    status: getStatusString(item.tenantUserStatusId),
                };
            });

            setUserAccounts(processedResponse);
        } catch (error) {
            console.error('Error loading user accounts:', error);
            Toast.error(getText('msg_0f423587405a'));
        }
    };

    const getStatusString = (status) => {
        switch (status) {
            case 1:
                return 'Pending';
            case 2:
                return 'Not Started';
            case 3:
                return 'Active';
            case 4:
                return 'Inactive';
            case 5:
                return 'Disabled';
            case 6:
                return 'Locked';
            default:
                return 'Undefined';
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 1:
                return 'ing-badge-pending';
            case 2:
                return 'ing-badge-inv-sent';
            case 3:
                return 'ing-badge-active';
            case 4:
                return 'ing-badge-inactive';
            case 5:
                return 'ing-badge-disabled';
            case 6:
                return 'ing-badge-error';
            default:
                return 'ing-badge-inactive';
        }
    };

    const sortedUserAccounts = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue;
            let bValue;
            if (sortConfig.key === 'tenantUserStatusId') {
                aValue = getStatusString(a[sortConfig.key]);
                bValue = getStatusString(b[sortConfig.key]);
            } else if (sortConfig.key === 'agencyName') {
                aValue = String(a[sortConfig.key]);
                bValue = String(b[sortConfig.key]);
            } else if (sortConfig.key === 'lastActiveDate') {
                aValue = String(a[sortConfig.key]);
                bValue = String(b[sortConfig.key]);
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    let handleSortLastName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'lastName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'lastName',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortFirstName = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'firstName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'firstName',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortAgency = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'agencyName',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'agencyName',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortStatus = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'status',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'status',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    let handleSortLastActive = () => {
        if (sortConfig.direction === SortDirections.ASCENDING) {
            setSortConfig({
                key: 'lastActiveDate',
                direction: SortDirections.DESCENDING,
            });
        } else {
            setSortConfig({
                key: 'lastActiveDate',
                direction: SortDirections.ASCENDING,
            });
        }
    };
    const handlerRowClicked = id => {
        let newRowsClicked = rowsClicked;
        if (!rowsClicked.includes(id)) {
            newRowsClicked.push(id);
        } else {
            newRowsClicked.splice(newRowsClicked.indexOf(id), 1);
        }
        setRowsClicked([...newRowsClicked]);
    };

    const handleSave = () => {
        assignUsers(rowsClicked, clientId).then(response => {
            Toast.success(getText('msg_44b8326d49f8'));
            deselectRows(rowsClicked);
            loadUserAccounts();
        });
        /*rowsClicked.map((row) => {
            userAccounts.filter((userAccount, index) => {
                if (userAccount.id === row) {
                    deleteClientUser(individualId, userAccount.id).then((response) => {
                        Toast.success("User with email '" + userAccount.emailAddress + "' was unassigned successfully.");
                        deselectRows(rowsClicked);
                        loadUserAccounts();
                    })
                   console.log("adding "+rowsClicked);
                }
                return null;
            });
            return null;
        });*/
    };

    const deselectRows = rowsClicked => {
        rowsClicked.map(row => {
            userAccounts.filter(userAccount => {
                if (userAccount.id === row) {
                    document.getElementById(userAccount.id).checked = false;
                }
                return null;
            });
            return null;
        });
        setRowsClicked([]);
    };

    const navigateBack = () => {
        navigate(ROUTE_BUILDERS.clientsView(clientId), {
            state: { existingTab: 'Users' },
        });
    };

    return (
        <div
            className="page-content"
            style={{ height: '100%', overflow: 'auto' }}
        >
            <div className="card" style={{ minHeight: '100%' }}>
                <div className="card-body px-0 py-4">
                    {/* Page Header with Title and Action */}
                    <div className="d-flex justify-content-between">
                        {/* Page Title */}
                        <p className="page-title float-start d-inline card-title ms-4">
                            {getText('msg_968e004421ab')}
                        </p>
                        {/* Page Actions */}
                        <div className="float-end d-flex align-items-center me-4">
                            <p
                                type="button"
                                onClick={navigateBack}
                                className="ing-text-buttons d-inline ms-3"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </p>
                        </div>
                    </div>
                    <form
                        onSubmit={handleFormSubmit}
                        className="table-responsive-xxl w-100 overflow-visible"
                    >
                        <section className="card-title m-4 d-flex flex-wrap mb-5 overflow-visible">
                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-3 mb-3 me-3 ${styles2.indPopInput}`}
                            >
                                <label htmlFor="lastName">{getText('msg_863cb39fbe7d')}</label>
                                <input
                                    onKeyDown={handleKeyDown}
                                    id="lastName"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={searchParams.lastName}
                                    onChange={handleInputChange}
                                    name="lastName"
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-3 mb-3 me-3${styles2.indPopInput}`}
                            >
                                <label htmlFor="firstName">{getText('msg_b6ea992aab46')}</label>
                                <input
                                    onKeyDown={handleKeyDown}
                                    id="firstName"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={searchParams.firstName}
                                    onChange={handleInputChange}
                                    name="firstName"
                                />
                            </div>
                            <div
                                className={`d-flex form-group col-12 col-sm-12 col-md-12 col-lg-3 mb-3 me-3 ${styles2.indPopInput}`}
                                style={{
                                    alignSelf: 'flex-end',
                                    paddingBottom: '3px',
                                }}
                            >
                                <div>
                                    <span
                                        onClick={handleSubmit}
                                        type="button"
                                        className="ms-3 me-4  button-base ing-button-primary-left "
                                    >
                                        <i className="bi bi-search me-1" />
                                        {getText('msg_bce06414177f')}
                                    </span>
                                </div>
                            </div>

                            {/*<div
                                        className={`form-group col-12 col-sm-12 col-md-12 col-lg-3 mb-3 pe-2 ${styles2.indPopInput}`}
                                    >
                                        <label htmlFor="agency">Agency</label>
                                        <input
                                            onKeyDown={handleKeyDown}
                                            id="agency"
                                            type="text"
                                            maxLength="50"
                                            className={`form-control`}
                                            value={searchParams.agency}
                                            onChange={handleInputChange}
                                            name="agency"
                                        />
                                    </div>*/}

                            {/*<div className="float-end d-flex align-items-center col-12 col-sm-12 col-md-12 col-lg-12 justify-content-end">
                                <div to="new-portal-page">
                                    <span
                                        onClick={handleSubmit}
                                        type="button"
                                        className="ms-3 me-4  button-base ing-button-primary-left "
                                    >
                                        <i className="bi bi-search me-1" />
                                        Search
                                    </span>
                                </div>
                            </div>*/}
                        </section>
                    </form>
                    {/* Screen Actions */}
                    <div className="float-end d-flex align-items-center pb-3">
                        <span
                            type="button"
                            onClick={clearFilters}
                            className="ing-text-buttons mx-3"
                        >
                            <i className="bi bi-arrow-counterclockwise me-2" />
                            {getText('msg_381c4bf1d409')}
                        </span>
                    </div>

                    <div className="table-responsive-xxl w-100">
                        {/* Template List Table */}
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="text-center">
                                        <svg
                                            width="19"
                                            height="19"
                                            viewBox="0 0 19 19"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5.2 9.3H12.8"
                                                stroke="#0070FF"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="17"
                                                height="17"
                                                rx="4"
                                                stroke="#0070FF"
                                            />
                                        </svg>
                                    </th>
                                    <th>
                                        <span>{getText('msg_863cb39fbe7d')}</span>
                                        <span
                                            onClick={handleSortLastName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'lastName' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() =>
                                                openModal('lastName')
                                            }
                                            className={styles.cursorPointer}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'lastName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                //onSelectAll={handleSelectAll}
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_b6ea992aab46')}</span>
                                        <span
                                            onClick={handleSortFirstName}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'firstName' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() =>
                                                openModal('firstName')
                                            }
                                            className={styles.cursorPointer}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'firstName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                //onSelectAll={handleSelectAll}
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_5c47e26c6ad2')}</span>
                                        <span
                                            onClick={handleSortAgency}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'agencyName' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() =>
                                                openModal('agencyName')
                                            }
                                            className={styles.cursorPointer}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'agencyName' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                //onSelectAll={handleSelectAll}
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_bae7d5be7082')}</span>
                                        <span
                                            onClick={handleSortStatus}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'status' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => openModal('status')}
                                            className={styles.cursorPointer}
                                        >
                                            <i
                                                className="bi bi-funnel"
                                                style={{ fontSize: '0.7em' }}
                                            ></i>
                                        </span>
                                        {activeModal === 'status' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                //onSelectionChange={(option) => handleFilterChange(activeModal, option)}
                                                //onSelectAll={handleSelectAll}
                                                onApply={handleApply}
                                                onClose={closeModal}
                                                getString={getStatusString}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span>{getText('msg_0eb2b938ad2e')}</span>
                                        <span
                                            onClick={handleSortLastActive}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'lastActiveDate' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedUserAccounts.map(
                                    (userAccount, index) => (
                                        <tr
                                            key={userAccount.id}
                                            className={`${rowsClicked.includes(userAccount.id) ? 'clicked-class' : ''}`}
                                        >
                                            <td className="text-center">
                                                <input
                                                    id={userAccount.id}
                                                    onClick={() =>
                                                        handlerRowClicked(
                                                            userAccount.id,
                                                        )
                                                    }
                                                    className="form-check-input"
                                                    type="checkbox"
                                                />
                                            </td>
                                            <td className="item-page-name-static">
                                                {userAccount.lastName}
                                            </td>
                                            <td className="item-page-name-static">
                                                {userAccount.firstName}
                                            </td>
                                            <td className="item-page-changed-by">
                                                {userAccount.agencyName || '-'}
                                            </td>
                                            <td className="item-page-changed-by">
                                                <button
                                                    className={getStatusClass(
                                                        userAccount.tenantUserStatusId,
                                                    )}
                                                >
                                                    {getStatusString(
                                                        userAccount.tenantUserStatusId,
                                                    )}
                                                </button>
                                            </td>
                                            <td
                                                className="item-page-changed-by"
                                                style={{
                                                    fontVariantNumeric:
                                                        'tabular-nums',
                                                }}
                                            >
                                                {getDateFromZulu(
                                                    userAccount.lastActiveDate,
                                                ) || '-'}
                                            </td>
                                        </tr>
                                    ),
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {userAccounts.length <= 0 && searchInitialized
                            ? 'No matching user accounts found'
                            : ''}
                    </p>
                    <div className={`form-group mb-3 me-5 flex-wrap`}>
                        <div
                            onClick={handleSave}
                            type="button"
                            style={{
                                display: 'block',
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                marginTop: '38px',
                                marginBottom: '38px',
                            }}
                            className="float-end button-base ing-button-primary-left"
                        >
                            {getText('msg_efc007a393f6')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssignUser;
